import { useUserContext } from "@contexts/UserContext/index.context";
import {
  AdminPanelSettings,
  CorporateFare,
  Description,
  DriveEta,
  Home,
  KeyboardArrowDown,
  Person,
  Tune,
} from "@mui/icons-material";
import type { MenuProps } from "@mui/material";
import {
  AppBar,
  alpha,
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  styled,
  Toolbar,
} from "@mui/material";
import { UserRank, UserRole } from "@types_def/models/user.type";
import { RoutePaths } from "@utils/constants/routes";
import type { MouseEvent } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AnySearch from "../../AnySearch";
import ChangeLangauge from "../../ChangeLanguage";
import { MainHeaderButton } from "../../NavigationLink";
import UserAvatar from "../UserAvatar";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    textTransform: "capitalize",
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
        ...theme.applyStyles("dark", {
          color: "inherit",
        }),
      },
      "&:active": {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

function AdminMenu({ rank }: { rank: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (route: string) => {
    handleClose();
    void navigate(route);
  };

  return (
    <>
      <Button
        id="admin-menu-button"
        aria-controls={open ? "admin-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        startIcon={<AdminPanelSettings />}
        endIcon={<KeyboardArrowDown />}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          },
        }}
      >
        {t("layouts.header.admin")}
      </Button>
      <StyledMenu
        id="admin-menu"
        slotProps={
          {
            // list: {
            //   'aria-labelledby': 'admin-menu-button',
            // },
          }
        }
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => handleMenuClick(RoutePaths.adminDriversTableacceptedRequests)}
          disableRipple
        >
          <Person />
          {t("layouts.header.drivers")}
        </MenuItem>
        <MenuItem onClick={() => handleMenuClick(RoutePaths.adminOptions)} disableRipple>
          <Tune />
          Options
        </MenuItem>
        <MenuItem onClick={() => handleMenuClick(RoutePaths.adminOrganization)} disableRipple>
          <CorporateFare />
          Organisations
        </MenuItem>
        {rank === UserRank.SUPER_ADMIN && (
          <MenuItem onClick={() => handleMenuClick(RoutePaths.adminManageAdmins)} disableRipple>
            <AdminPanelSettings />
            {t("layouts.header.managers")}
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => handleMenuClick(`${RoutePaths.adminInvoicesDriver}/subscription`)}
          disableRipple
        >
          <Description />
          {t("layouts.header.invoices")}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => handleMenuClick(RoutePaths.adminTerms)} disableRipple>
          <AdminPanelSettings />
          {t("layouts.header.terms")}
        </MenuItem>
      </StyledMenu>
    </>
  );
}

function MainHeader() {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const navigate = useNavigate();
  const role = userState?.["custom:role"]?.toLowerCase();
  const isAdmin = role === UserRole.ADMIN.toLowerCase();
  const rank = Number(userState?.["custom:rank"]);

  if (!userState?.connected || !role || !rank) {
    throw new Error("an issue occured while fetching user state");
  }

  const goToDashboard = () => {
    void navigate(`${role}/dashboard`);
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "primary.main", // Replaces var(--primary-color)
        color: "#fff",
        height: "75px",
        padding: "1rem",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100%",
          padding: 0,
          minHeight: "unset !important", // Override default minHeight
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            component="img"
            src="/images/logo.svg"
            alt="logo"
            onClick={goToDashboard}
            sx={{
              marginRight: "2rem",
              paddingRight: "2rem",
              borderRight: "2px solid #fff",
              cursor: "pointer",
              height: "20px", // Adjust as needed to match logo size
              textTransform: "capitalize",
            }}
          />
          <MainHeaderButton
            to={`/${role}/dashboard`}
            includes={RoutePaths.dashboard}
            icon={<Home fontSize="small" />}
          >
            {t("layouts.header.dashboard")}
          </MainHeaderButton>

          <MainHeaderButton
            to={`/${role}/pilot/delivery-table/${role === UserRole.ADMIN.toLowerCase() ? "requested" : "confirmed"}`}
            includes={RoutePaths.deliveryTable}
            icon={<DriveEta fontSize="small" />}
          >
            {t("layouts.header.deliveries")}
          </MainHeaderButton>
          {role === UserRole.CLIENT.toLowerCase() && rank <= UserRank.ORGANIZATION_MANAGER && (
            <MainHeaderButton
              to={RoutePaths.clientOrganizationAddMember}
              includes={RoutePaths.organization}
              icon={<CorporateFare fontSize="small" />}
            >
              {t("layouts.header.organization")}
            </MainHeaderButton>
          )}
          <Box flex={1} width="100%" />
          {role === UserRole.ADMIN.toLowerCase() && <AdminMenu rank={rank} />}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            ml: { md: 10 },
          }}
        >
          <AnySearch isAdmin={isAdmin} />
          <ChangeLangauge />
          <UserAvatar userState={userState} />
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default MainHeader;
